<script setup>
import { reactive, ref, onMounted, watch } from 'vue';
import { getProfile, updateProfile } from '@/api/profileApi.js';
import { getRoles } from '@/api/auth/authApi.js';
import { useAuthStore } from '@/stores/auth.js';
import { useNotificationStore } from '@/stores/notification.js';
import { MESSAGES } from '@/constants/messages';

// user情報
const auth = useAuthStore();

const props = defineProps({
  // 編集対象のユーザid
  id: {
    type: String,
    required: true,
  },
});

// ストアを呼び出す
const store = useNotificationStore();
const isSubmitting = ref(false);

const roles = ref([]);
const form = reactive({
  id: '',
  username: '',
  role_id: '',
  birthday: '',
});

// ユーザーデータを読み込む関数
async function loadUserData(userId) {
  if (!userId) return;
  const data = await getProfile(userId);
  if (data) {
    form.id = userId;
    form.username = data.userName;
    form.role_id = data.roleId;
    form.birthday = data.birthday;
  }
}

onMounted(async () => {
  // ロール一覧は一度だけ取得
  roles.value = await getRoles();
  // 初期データの読み込み
  await loadUserData(props.id);
});

// IDが変わったら再読み込み（管理画面で別のユーザーを選んだ時用）
watch(
  () => props.id,
  async (newId) => {
    await loadUserData(newId);
  },
);

const handleUpdate = async () => {
  // 二重送信防止
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await updateProfile(form);
    store.success(MESSAGES.SUCCESS.UPDATE);
  } catch (error) {
    store.error(MESSAGES.ERROR.UPDATE);
    console.error('更新失敗:', error);
  } finally {
    isSubmitting.value = false; // 成功しても失敗してもローディング終了
  }
};
</script>

<template>
  <form
    @submit.prevent="handleUpdate"
    class="p-10 grid grid-cols-[1fr_2fr] gap-4 items-center justify-around border border-gray-500 rounded-sm"
  >
    <label for="username"><span>ユーザー名</span></label>
    <input
      v-model="form.username"
      class="border border-gray-500 rounded-sm px-2 py-1 focus:outline-emerald-400"
      type="text"
    />

    <label for="role" v-if="auth.isAdmin"><span>ロール</span></label>
    <select
      v-if="auth.isAdmin"
      :disabled="props.id == auth.user.id"
      class="border border-gray-500 rounded-sm px-2 py-1 focus:outline-emerald-400 disabled:bg-gray-100 disabled:text-gray-500/50 disabled:border-gray-500/30"
      v-model="form.role_id"
    >
      <option v-for="role in roles" :key="role.id" :value="role.id">
        {{ role.name }}
      </option>
    </select>

    <label for="birthday"><span>誕生日</span></label>
    <input
      v-model="form.birthday"
      class="border border-gray-500 rounded-sm px-2 py-1 focus:outline-emerald-400"
      type="date"
    />

    <button
      class="relative flex items-center justify-center m-auto col-span-2 bg-emerald-500 p-2 text-white rounded-4xl cursor-pointer hover:bg-emerald-600 w-50 h-10 disabled:bg-emerald-300 disabled:cursor-not-allowed overflow-hidden transition-all"
      type="submit"
      :disabled="isSubmitting"
    >
      <div
        v-if="isSubmitting"
        class="absolute inset-0 flex items-center justify-center bg-emerald-500 rounded-4xl"
      >
        <svg
          class="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <span :class="{ inisShow: isSubmitting }">
        {{ auth.isAdmin ? '管理者が更新' : 'プロフィールを更新' }}
      </span>
    </button>
  </form>
</template>
